import React from 'react';
import { styled } from '@ff/ui';
import { useHistory } from 'react-router-dom';
import Box from '@ff/ui/Box';
import Button from '@ff/ui/Button';
import Image from '@ff/ui/Image';
import Text from '@ff/ui/Text';
import url from 'client/utils/url';
import useConfig from 'client/hooks/useConfig';
import { VehicleModel } from 'client/hooks/queries/useVehicleModels';
import modelCodes from 'client/constants/modelCode';
import useVehicleModels from 'client/hooks/queries/useVehicleModels';
import useVehicleModelDetails from 'client/hooks/queries/useVehicleModelDetails';
import Loader from 'client/components/Loader';
import ModelFeatures from './ModelFeatures';
import useUser from 'client/hooks/queries/useUser';
import useOrderDetail from 'client/hooks/queries/useOrderDetail';
import UIError from 'client/utils/UIError';

interface ModelProps {
  vehicleModels: VehicleModel[];
  modelCode: string;
  order?: {
    modelId: number;
    modelCode: string;
    isOnWaitlist: boolean;
    ff91AllianceEligibility: boolean;
    options: {[key: string]: string }
  }
  onSelect: (modelCode: string, options?: {[key: string]: string}) => void;
}

interface ModelSelectionProps {
  onSelected: () => void;
}

export default function ModelSelection(props: ModelSelectionProps) {
  const { onSelected } = props;
  const history = useHistory();
  const qsParams = useConfig();
  const { ORDER: orderNum } = qsParams;

  const { vehicleModels, loading: loadingVehicleModels } = useVehicleModels();
  const { user, loading: loadingUser } = useUser();
  const { order, loading: loadingOrder } = useOrderDetail(orderNum as string, {
    skip: !orderNum?.length || !user
  });

  if (loadingVehicleModels || loadingUser || loadingOrder) {
    return <Loader />;
  }

  let currentOrder;
  if(order){
    let options;
    order.vehicleConfigurationCore?.vehicleConfigurationItems?.forEach(i => {
      if(!options){
        options = {};
      }
      options[i.vehicleOption.vehicleOptionType.categoryCode] = i.vehicleOption.vehicleOptionType.typeCode;
    })

    currentOrder = {
      modelId: order.vehicleModel.id,
      modelCode: order.vehicleModel.fullModelCode,
      isOnWaitlist: order.isOnWaitlist,
      ff91AllianceEligibility: order.canUpgradeEdition, // currently the canUpgradeEdition is used for ff91 alliance eligibility flag
      options, 
    }
  }

  const handleModelSelected = (selectedModelCode: string, options?: {[key: string]: string}) => {
    onSelected();

    const qs = options ? {
      ...qsParams,
      ...options,
      VEH: selectedModelCode,
    } : {
      ...qsParams,
      VEH: selectedModelCode,
      EXC: undefined,
      WHL: undefined,
      INT: undefined,
    };
    history.replace(url.getPageNavigationV3('options', qs));
  }

  return (
    <Container>
      {vehicleModels?.filter(vm => vm.fullModelCode !== 'FF91SNA').map(vm => (
        <Model
          key={vm.fullModelCode}
          vehicleModels={vehicleModels}
          modelCode={vm.fullModelCode}
          onSelect={handleModelSelected}
          order={currentOrder}
        />
      ))}
    </Container>
  );
}

function Model(props: ModelProps){
  const {
    vehicleModels,
    modelCode,
    order,
    onSelect,
  } = props;

  const vehicleModel = vehicleModels.find(v => v.fullModelCode === modelCode);
  if(!vehicleModel){
    throw new UIError('The vehicle model cannot be null.');
  }

  const ff91AllianceModel = vehicleModels.find(v => v.fullModelCode === modelCodes.FF91FuturistAlliance.fullModelCode);
  if(!ff91AllianceModel){
    throw new UIError('The ff91 alliance model cannot be null.');
  }

  const {
      id: modelId,
      name: modelName,
      vehicleOverviewAssets: {
        coverImage: {
          desktop: coverImage
        }
      },
    } = vehicleModel;

  const currentExc = order?.options?.['EXC']
  const currentModelCode = order?.modelCode;
  const currentOptions = order?.options;

  const isCurrent = currentModelCode === modelCode;

  const { vehicleModelDetails, loading: loadingVehicleModelDetails } =
    useVehicleModelDetails(modelId, {
      skip: !isCurrent || !currentExc 
    });

  if (loadingVehicleModelDetails) {
    return <Loader />;
  }

  let image;
  if(vehicleModelDetails && currentExc){
    image =
      vehicleModelDetails.vehicleConfigurationAssets.exterior.color.typeValues.find(
        (v) => v.code === currentExc 
      )?.images[0];
  }
  image = image || coverImage;

  const handleSelectClicked = () => {
    onSelect(modelCode, isCurrent ? currentOptions : undefined);
  }

  return (
    <ModelContainer isCurrent={isCurrent}>
      <Title>
        <ModelName>{modelName}</ModelName>
      </Title>
      <CarImageContainer>
        <CarImage src={image}/>
      </CarImageContainer>
      <FeaturesContainer>
        <DesktopModelFeatures>
          <ModelFeatures
            modelId={modelId}
            align='center'
            fontSize='16px'
            color='black'
          />
        </DesktopModelFeatures>
        <MobileModelFeatures>
          <ModelFeatures
            modelId={modelId}
            align='center'
            fontSize='13px'
            color='black'
          />
        </MobileModelFeatures>
      </FeaturesContainer>
      <ActionButton
        variant='primary'
        fullWidth
        onClick={handleSelectClicked}>
        <ButtonText>{ isCurrent ? 'The current choice' : 'Select' }</ButtonText>
      </ActionButton>
    </ModelContainer>
  );
}

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  padding: '84px 0px 48px',
  minWidth: '320px',

  [theme.mediaQueries.md]: {
    gap: '48px',
    padding: '84px 48px 48px',
  },

  [theme.mediaQueries.lg]: {
    flexDirection: 'row',
  }
}));

const ModelContainer = styled(Box)<{ isCurrent: boolean }>(({ theme, isCurrent }) => ({
  maxWidth: '500px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: isCurrent ? 'rgba(0,0,0, 0.05)' : 'rgba(0,0,0, 0.02)',
  padding: '48px 24px 24px',

  [theme.mediaQueries.md]: {
    borderRadius: '16px',
  }
}));

const Title = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '67px',
});

const ModelName = styled(Text)(({theme}) => ({
  fontSize: '20px',
  lineHeight: '20px',

  [theme.mediaQueries.sm]: {
    fontSize: '34px',
    lineHeight: '39.84px',
  }
}));


const CarImageContainer = styled(Box)({
  position: 'relative',
  top: '-36px',
  overflow: 'hidden',
  width: '100%',
});

const CarImage = styled(Image)({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  aspectRatio: '16/9',
  transform: 'scale(1.8)',
  alignSelf: 'center',
  margin: '0 auto',
});

const FeaturesContainer = styled(Box)({
  flex: '1',
});

const DesktopModelFeatures = styled(Box)(({theme}) => ({
  display: 'none',

  [theme.mediaQueries.sm]: {
    display: 'block',
  }
}))

const MobileModelFeatures = styled(Box)(({theme}) => ({
  display: 'block',

  [theme.mediaQueries.sm]: {
    display: 'none',
  }
}))

const ActionButton = styled(Button)(({ theme }) => ({
  marginTop: '50px',
  height: '42px',
  padding: '0px',
  backgroundColor: theme.colors.black, 
  ':disabled': {
    color: theme.colors.black,
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.white,
  },
  ':hover:disabled': {
    color: theme.colors.black,
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.white,
  },

  [theme.mediaQueries.sm]: {
    height: '52px',
  }
}));

const ButtonText = styled(Text)(({ theme }) => ({
  height: '42px',
  fontSize: '16px',
  lineHeight: '42px',
  textDecoration: 'none',
  textTransform: 'none',

  [theme.mediaQueries.sm]: {
    height: '52px',
    fontSize: '20px',
    lineHeight: '52px',
  }
}))
